import { useQueries } from "@tanstack/react-query";
import type { PermissionCode } from "@koclukpanel/authz";
import { Ikon, Iskelet, cn, type IkonAdi } from "@koclukpanel/ui";
import { ApiHatasi, api } from "./api";

/**
 * YONETICI ANA EKRANI — "neye bakmaliyim"
 *
 * ================================================================
 * BILGI PANOSU DEGIL, BEKLEYEN IS LISTESI
 * ================================================================
 * `analiz.controller.ts` uzman ekrani icin ayni karari yaziyor:
 * "ana ekran BILGI PANOSU DEGIL, is listesidir". Ayni gerekce
 * yonetici icin de gecerli — hatta daha fazla: aktif ogrenci sayisi
 * ya da aylik tahsilat gunde bir bakilacak seyler, PANELIN ACILIS
 * EKRANINDA duracak seyler degil. Acilis ekraninin cevaplamasi
 * gereken tek soru "simdi ne yapmam lazim".
 *
 * ================================================================
 * DORT AYRI "BOS": HICBIRI AYNI SEY DEGIL
 * ================================================================
 *   sayi > 0   -> is var. Kart onem rengini tasiyor.
 *   sayi = 0   -> is YOK ve bu bir BASARI. Kart CIZILIYOR, ama soluk.
 *   403 / 404  -> bu senin isin degil. Kart HIC cizilmiyor.
 *   hata       -> KONTROL EDILEMEDI. Kart ciziliyor ve bunu soyluyor.
 *
 * ================================================================
 * SIFIR KARTI ARTIK GIZLENMIYOR — 04.09.2026 DAVRANIS DEGISIKLIGI
 * ================================================================
 * Onceki hâlde `sayi === 0` olan satir hic cizilmiyordu; gerekce
 * "sakin ekran iyi haberdir" idi. Cizim (`BugunYonetici.dc.html`)
 * bunun tersini soyluyor ve gerekcesini de yaziyor:
 *
 *   "SIFIR KART: gizlenmiyor. Sifir bir BASARI ve gorunmesi lazim,
 *    ama gorsel agirligi dolularin onune gecmemeli."
 *
 * Fark su: gizlenen sifir ile HIC SORULMAMIS sifir ekranda ayni
 * goruntuyu veriyordu. Kullanici "izin talebi yok" ile "izin
 * taleplerine bakilmadi" arasindaki farki goremiyordu. Cizilen bir
 * "temiz" karti bu iki durumu ayiriyor: kart varsa BAKILDI.
 *
 * Gorsel agirlik farki da cizimden birebir: temiz kartin GOLGESI YOK,
 * kenari `notr-100`, sayisi `notr-500`. Yani yuzeyden bir kademe
 * geride duruyor.
 *
 * ================================================================
 * KAC KART KALIRSA KALSIN, HICBIRI KALMAZSA EKRAN SUSUYOR
 * ================================================================
 * Bes kaynagin BESI DE 403 donerse bilesen `null` donuyor — "bekleyen
 * is yok" DEMIYOR. Cizimdeki tasarim notu bu karari acikca veriyor:
 * 403 "is yok" degil "bu bolum sana ait degil" demek ve bos durum
 * gostermek yanlis olurdu. Eski hâl ikisini tek cumleye indiriyordu.
 */

type Onem = "acil" | "dikkat" | "bilgi";

export interface Kaynak {
  anahtar: string;
  ikon: IkonAdi;
  ekran: string;
  onem: Onem;
  /** Sayinin ALTINDAKI cumle — sayiyi ICERMEZ, kart onu ayri ciziyor. */
  etiket: string;
  /**
   * Sayinin yanindaki birim ("taksit").
   *
   * Yalnizca cizimin verdigi yerde var. Her karta birim uydurmak,
   * "1 rapor" gibi zaten etikette gecen bir kelimeyi tekrar ederdi.
   */
  birim?: string;
  /**
   * UCUN ISTEDIGI IZIN — soru SORULMADAN once bakiliyor.
   *
   * ================================================================
   * NEDEN VAR: HER RET DENETIM KAYDINA DUSUYOR
   * ================================================================
   * Bu ekran once BES ISTEGI de atiyor ve yetkisi olmayanlarin 403'unu
   * sessizce eliyordu. Davranis dogru gorunuyordu — kullanici yanlis
   * bir sey gormuyordu — ama `authz.guard.ts` her reddi
   * `erisimReddedildi` ile denetim kaydina yaziyor.
   *
   * Canli olculdu (05.09.2026): bir kocun her "Bugun" acilisi UC
   * "erisim reddedildi" olayi uretiyordu. Gun boyunca birikince,
   * GERCEK bir yetkisiz erisim denemesi bu gurultunun icinde
   * kayboluyor — yani bir kolaylik kusuru degil, denetim kaydinin
   * kullanilabilirligine verilen zarar.
   *
   * Emre'nin karari (06.09.2026): "Yetkisi olmayani hic sormasin."
   *
   * ⚠️ Bu bir GUVENLIK kontrolu DEGIL, gurultu onleme. Yetkiyi sunucu
   * uyguluyor; buradaki kontrol yalnizca reddedilecegi bilinen istegi
   * atmamak icin. Ikisini karistirmak, istemciye guvenmek olurdu.
   */
  izin: PermissionCode | readonly PermissionCode[];
  getir: () => Promise<number>;
}

/**
 * Yanit bicimleri farkli; hepsinden SAYI cikaran tek yer.
 *
 * ================================================================
 * ANLAMADIGI CEVAP ICIN "0" DONMUYOR — ATIYOR
 * ================================================================
 * Onceki hali tanimadigi bicimde `0` donduruyordu. Bu, tam da bu
 * ekranin kacindigi seydi: birkac satir asagida "yetki yoksa SESSIZ,
 * cunku finansa bakmayan birine '0 gecikmis tahsilat' demek olmayan
 * bir guven verir" yaziyor. Ayni cumle burada da gecerli — cevabi
 * cozemedigimizde kart "temiz" yesiliyle acilir ve yonetici bakmasi
 * gereken yere bakmaz.
 *
 * Atinca istek HATA sayiliyor ve ekranin ZATEN VAR OLAN dorduncu
 * hali devreye giriyor: kesik kenar, taranmis zemin, sayi yerine
 * "—". Yani yeni bir gorunum eklemek gerekmedi; olan hal dogru yere
 * baglandi.
 *
 * Bu yol yalnizca bir ucun yanit BICIMI degistiginde acilir —
 * nadir, ama sessiz kaldiginda fark edilmeyen turden.
 */
const sayiyaCevir = (d: unknown): number => {
  if (Array.isArray(d)) return d.length;
  const o = d as { toplam?: number };
  if (typeof o?.toplam === "number") return o.toplam;
  throw new Error("Sayı okunamadı: beklenmeyen yanıt biçimi.");
};

/*
  SIRA CIZIMDEN: onay · tahsilat · talep · izin · aday. Ayni sira
  hem onem sirasi (acil, acil, dikkat, dikkat, bilgi) hem de cizimdeki
  kart sirasi — ikisi ortustugu icin ayri bir siralama kurali
  gerekmiyor, asagidaki `sirala` yalnizca 403 dusenler cikinca
  bosluklari kapatiyor.
*/
export const KAYNAKLAR: readonly Kaynak[] = [
  {
    anahtar: "onay",
    ikon: "rapor",
    ekran: "onay",
    onem: "acil",
    etiket: "rapor onay bekliyor",
    izin: "rapor.veli.onayla",
    /**
     * ================================================================
     * `/raporlar/onay-bekleyenler` — `/onaylar/bekleyenler` DEGIL
     * ================================================================
     * Ilk yazimda `/onaylar/bekleyenler` yaziyordu ve UC SEY birbirini
     * tutmuyordu: sayilan sey F-APR'nin GENEL onay talebi kuyrugu,
     * yazilan cumle "rapor onay bekliyor", tiklaninca acilan ekran ise
     * veli raporu kuyrugu (`OnayKuyrugu.tsx`).
     *
     * Ucu de sessizdi: her iki uc de dizi donuyor, sayi da uretiyor.
     * Yalnizca YANLIS sayiyi uretiyordu.
     *
     * Genel onay mekanizmasinin (F-APR) hic ekrani yok; oldugunda
     * kendi kartini alir. O zamana kadar bu kart gorevini yaptigi
     * ekranla ayni veriyi saymali.
     */
    getir: async () => sayiyaCevir(await api("/raporlar/onay-bekleyenler")),
  },
  {
    anahtar: "tahsilat",
    ikon: "finans",
    ekran: "finans",
    onem: "acil",
    etiket: "tahsilat gecikti",
    birim: "taksit",
    izin: "finans.tahsilat.goruntule",
    getir: async () => sayiyaCevir(await api("/finans/tahsilat-kuyrugu")),
  },
  {
    anahtar: "talep",
    ikon: "gorusme",
    ekran: "talepler",
    onem: "dikkat",
    etiket: "talep cevap bekliyor",
    // Ucun kendisi UC izinden BIRINI ariyor; liste birebir ayni olmali,
    // yoksa ekran soru sormayi atlar ve kart sessizce kaybolur.
    izin: ["operasyon.talep.olustur", "operasyon.talep.karara_bagla", "finans.talep.karara_bagla"],
    // `PENDING` — `ApprovalStatus`. Ilk yazimda `OPEN` yazmistim ve
    // sessizce BOS donuyordu: `OPEN` `SupportStatus` degeri, yani
    // BASKA bir kavramin durumu. Sunucu bilinmeyen degeri suzgecten
    // dusuruyor, hata vermiyor — bu yuzden ancak gercek bir talep
    // olusturunca fark edildi.
    getir: async () =>
      sayiyaCevir(await api("/operasyon/talepler", { sorgu: { durum: "PENDING" } })),
  },
  {
    anahtar: "izin",
    ikon: "takvim",
    ekran: "operasyon",
    onem: "dikkat",
    etiket: "izin talebi karar bekliyor",
    izin: "operasyon.izin.talep",
    getir: async () =>
      sayiyaCevir(await api("/operasyon/izinler", { sorgu: { durum: "PENDING" } })),
  },
  {
    anahtar: "aday",
    ikon: "aday",
    ekran: "adaylar",
    onem: "bilgi",
    etiket: "aday ilk temas bekliyor",
    izin: "crm.aday.goruntule",
    getir: async () => sayiyaCevir(await api("/crm/adaylar", { sorgu: { durum: "NEW" } })),
  },
];

/*
  ================================================================
  KART GORUNUMU — UNİKOÇ ANASAYFA KARTI (sürüm 9a B2; tarama-1 §3.1, §2.1, §2.10)
  ================================================================
  Eski paneldeki bilgi kartı: ortalı, üstte büyük çizgi ikon (mor), kalın büyük başlık, gri
  açıklama, altta birincil degrade düğme. Zemin KART'A ÖZEL TONDAN beyaza doğru üstten degrade
  (mavi / yeşil / sarı / pembe). Ton ÖNEMİ taşıyor — renk tek başına anlam değil, rozet ve metin
  yerinde (kod standardı): acil → pembe, dikkat → sarı/krem, bilgi → mavi, temiz → yeşil.
  Tonlar paylaşılan paletten (`hata-50`, `uyari-50`, `marka-50`, `onay-50`); ham renk yok.
  Sayı, sayfanın en büyük şeyi: UNİKOÇ'taki "20 / 25" gibi 28 px kalın.
*/
const ONEM_GORUNUMU: Record<Onem, { ton: string; rozet: string; etiket: string }> = {
  acil: { ton: "from-hata-50", rozet: "bg-hata-50 text-hata-700", etiket: "Acil" },
  dikkat: { ton: "from-uyari-50", rozet: "bg-uyari-50 text-uyari-800", etiket: "Dikkat" },
  bilgi: { ton: "from-marka-50", rozet: "bg-notr-100 text-notr-600", etiket: "Bilgi" },
};

type Durum = "var" | "temiz" | "hata";

interface Kart {
  kaynak: Kaynak;
  durum: Durum;
  sayi: number;
}

function KaynakKarti({ kart, onAc }: { kart: Kart; onAc: () => void }) {
  const g = ONEM_GORUNUMU[kart.kaynak.onem];
  const temiz = kart.durum === "temiz";
  const hata = kart.durum === "hata";

  return (
    <button
      type="button"
      onClick={onAc}
      className={cn(
        "relative flex w-full flex-col items-center gap-2 rounded-[1.5rem] border p-6 text-center",
        "to-notr-0 bg-linear-to-b",
        "ease-cikis transition-[box-shadow,transform] duration-(--sure-hizli)",
        "hover:shadow-yukselen hover:-translate-y-px",
        "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        hata
          ? // Dorduncu hal RENKLE degil DOKUYLA ayriliyor: kesikli kenar, taranmis zemin, golge
            // YOK. Golge "bu bir yuzey" der; olculemedi bir yuzey degil, bir bosluk.
            "border-notr-400 taranmis-zemin border-dashed"
          : cn(
              "border-mor-kenar shadow-(--golge-unk-kart)",
              // Temiz kart bir kademe geride: yesil ton, golge hafif.
              temiz ? "from-onay-50" : g.ton,
            ),
      )}
    >
      {/* Rozet kartin KOSESINDE: ortali govdeyi itmiyor. */}
      {temiz ? (
        /*
          TEMIZ ROZETI otekilerden BASKA turlu duruyor: zemini yok, buyuk harf degil, `font-medium`.
          "Acil" bir uyaridir ve bagirir, "temiz" bir teyittir ve bagirmaz.
        */
        <span className="rounded-alan text-onay-700 absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium">
          <Ikon ad="tamam" boyut="kucuk" />
          temiz
        </span>
      ) : (
        <span
          className={cn(
            "rounded-alan absolute top-4 right-4 inline-flex px-2 py-0.5 text-xs font-semibold tracking-[0.02em] uppercase",
            hata ? "bg-notr-100 text-notr-600" : g.rozet,
          )}
        >
          {hata ? "Ölçülemedi" : g.etiket}
        </span>
      )}

      <Ikon
        ad={hata ? "olculemedi" : kart.kaynak.ikon}
        className={cn("mt-2 size-11", hata ? "text-notr-400" : "text-mor-500")}
      />

      <div className="flex items-baseline justify-center gap-2">
        {/*
          `leading-none`: sayinin satir yuksekligi kartin dikey ritmini bozmasin. Rakamlar
          `tabular-nums` (`:root`): kartlar yan yana dizilince sayilar hizali kaliyor.

          Hata halinde TIRE ciziliyor ve tire bir yer tutucu degil, bilginin kendisi: "burada
          olcum yok". Sifir yazsaydik olculmemis bir kaynagi temiz gostermis olurduk.
        */}
        <span
          className={cn(
            "text-[1.75rem] leading-none font-bold",
            hata || temiz ? "text-notr-600" : "text-metin-koyu",
          )}
        >
          {hata ? "—" : kart.sayi}
        </span>
        {!hata && kart.kaynak.birim && (
          <span className="text-metin-ikincil text-sm">{kart.kaynak.birim}</span>
        )}
      </div>

      <span className="text-metin-ikincil text-base">
        {hata ? `${kart.kaynak.etiket} — kontrol edilemedi` : kart.kaynak.etiket}
      </span>

      {/* Birincil degrade "dugme" gorunumu: kartin tamami tek dugme, ikinci bir dugme ic ice olmaz. */}
      <span
        aria-hidden="true"
        className={cn(
          "mt-auto rounded-[0.75rem] px-5 py-2 text-sm font-bold",
          "text-notr-0 bg-(image:--gradyan-unk-birincil) shadow-(--golge-unk-birincil)",
        )}
      >
        Görüntüle
      </span>
    </button>
  );
}

/**
 * Bugünün tarihi — "Salı, 4 Eylül".
 *
 * Saat dilimi AÇIKÇA `Europe/Istanbul` (CLAUDE.md kural 8). Tarayıcının
 * yerel saatine bırakılsaydı yurt dışındaki bir kullanıcı için gün
 * kayabilir ve ekran "bugün" derken başka bir günü yazabilirdi.
 */
function bugununTarihi(simdi: Date): string {
  const bicim = (secenek: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat("tr-TR", { timeZone: "Europe/Istanbul", ...secenek }).format(simdi);
  return `${bicim({ weekday: "long" })}, ${bicim({ day: "numeric", month: "long" })}`;
}

export function YoneticiBugun({
  onEkranSec,
  izinler,
}: {
  onEkranSec: (ekran: string) => void;
  /**
   * Cozulmus izinler (`/auth/ben`den). Kart bunlarla SUZULUYOR: soru
   * sorulmadan once "bu kisi bunu gorebilir mi" bakiliyor.
   *
   * Ekran kendi izin listesini tutmuyor; her kartin izni `KAYNAKLAR`
   * icinde, ucun dekoratoruyle ayni kodla yazili.
   */
  izinler: Record<string, string>;
}) {
  /*
    SORULACAK KAYNAKLAR — yetkisi olmayan HIC sorulmuyor.

    Suzme burada, `enabled` ile degil: `useQueries` yine de her kaynak
    icin bir sorgu nesnesi kurar ve dizinler kayardi. Listeyi once
    daraltmak, `sorgular[i]` ile `SORULACAK[i]` esligini korumanin en
    basit yolu — kayan bir dizin, yanlis karta yanlis sayi yazdirirdi.
  */
  const SORULACAK = KAYNAKLAR.filter((k) =>
    typeof k.izin === "string" ? k.izin in izinler : k.izin.some((kod) => kod in izinler),
  );

  const sorgular = useQueries({
    queries: SORULACAK.map((k) => ({
      queryKey: ["bugun", k.anahtar],
      queryFn: k.getir,
      // 403 tekrar denemekle gecmez; yetki iki deneme arasinda
      // degismiyor.
      retry: false,
      staleTime: 30_000,
    })),
  });

  if (sorgular.some((s) => s.isPending)) return <Iskelet satir={4} />;

  const kartlar: Kart[] = [];
  for (const [i, s] of sorgular.entries()) {
    const kaynak = SORULACAK[i]!;
    if (s.isError) {
      /*
        YETKI YOKSA SESSIZ: finansa bakmayan birine "0 gecikmis
        tahsilat" demek, olmayan bir guven veriyor.

        ARTIK IKINCI SAVUNMA: sorgu zaten izne gore suzuluyor, yani bu
        dala normalde HIC girilmiyor. Yine de duruyor — izin listesi
        ile ucun dekoratoru bir gun ayrisirsa (biri degisip oteki
        degismezse) ekran yanlis sayi degil, HIC kart gostermeli.
      */
      const yetkiYok =
        s.error instanceof ApiHatasi && (s.error.durum === 403 || s.error.durum === 404);
      if (!yetkiYok) kartlar.push({ kaynak, durum: "hata", sayi: 0 });
      continue;
    }
    const sayi = s.data ?? 0;
    kartlar.push({ kaynak, durum: sayi > 0 ? "var" : "temiz", sayi });
  }

  /*
    HICBIR KART KALMADIYSA EKRAN SUSUYOR — "bekleyen is yok" DEMIYOR.
    Bu noktaya yalnizca BES KAYNAGIN DA 403/404 dondugu durumda
    gelinir, yani kullanicinin bu bes isten hicbirine yetkisi yoktur.
    Ona "bekleyen is yok" demek, bakmadigi bir kuyruk hakkinda guvence
    vermek olurdu.
  */
  if (kartlar.length === 0) return null;

  const sirali = kartlar.sort((a, b) => {
    // OLCULEMEYEN EN USTTE: bilinmeyen bir kuyruk, bilinen bir acil
    // isten daha once gorulmeli — ne oldugunu bilmiyoruz.
    if ((a.durum === "hata") !== (b.durum === "hata")) return a.durum === "hata" ? -1 : 1;
    const p = { acil: 0, dikkat: 1, bilgi: 2 };
    return p[a.kaynak.onem] - p[b.kaynak.onem];
  });

  return (
    <section className="flex flex-col gap-5">
      {/*
        KUNYE SATIRI — cizimde "Salı, 28 Ağustos · 21 aktif öğrenci ·
        8 personel" yaziyor; BURADA YALNIZCA TARIH VAR.

        Iki sayi bilerek yazilmadi: ikisini de dogru veren bir uc yok.
        En yakini `/takvim/kapasite` ve o "uzmanlara atanmis ogrenci"
        sayiyor — "aktif ogrenci" ile ayni sey degil. Ustelik bu uclar
        kapsam suzgecinden geciyor, yani ayni cumle iki kullanicida iki
        farkli sey soylerdi. Yanlis bir sayi, hic sayi olmamasindan
        kotu: kimse dogrulamadan ona gore karar verir.

        Ekran BASLIGI ("Bugün") burada YOK cunku kabuk onu zaten
        `<h1>` olarak ciziyor (`Kabuk.tsx`); ikinci kez yazmak ekran
        okuyucuda ayni basligi iki kez duyururdu.
      */}
      <p className="text-metin-ikincil text-sm">{bugununTarihi(new Date())}</p>

      <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {sirali.map((k) => (
          <li key={k.kaynak.anahtar} className="flex">
            <KaynakKarti kart={k} onAc={() => onEkranSec(k.kaynak.ekran)} />
          </li>
        ))}
      </ul>
    </section>
  );
}
